/**
 * Notification Component
 * A React component that displays temporary notification messages with auto-dismiss functionality
 * Supports different types of notifications (success, error, info) with appropriate styling
 */

import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { removeNotification } from "../../store/notification-store";
import { XMarkIcon } from "@heroicons/react/24/solid";

/**
 * Individual notification component that auto-dismisses after 10 seconds
 * Displays a message with a close button and color-coded background based on type
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the notification
 * @param {string} props.message - Message to display in the notification
 * @param {('success'|'error'|'info')} props.type - Type of notification that determines styling
 * @returns {JSX.Element} Rendered notification component
 */
const Notification = ({ id, message, type }) => {
  /**
   * Redux dispatch function for managing notification state
   */
  const dispatch = useDispatch();

  /**
   * Effect hook to handle auto-dismiss functionality
   * Sets a timeout to remove the notification after 10 seconds
   * Cleans up timeout on component unmount
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(removeNotification(id));
    }, 10000);

    return () => clearTimeout(timer);
  }, [dispatch, id]);

  /**
   * Render section
   * Displays notification with conditional styling based on type
   */
  return (
    <div
      className={`p-4 mb-2 rounded-lg shadow-lg transition duration-300 
                ${
                  type === "success"
                    ? "bg-green-500"
                    : type === "error"
                      ? "bg-red-500"
                      : "bg-blue-500"
                } text-white flex justify-between items-center`}
    >
      <span>{message}</span>
      <button
        className="ml-4 px-2 rounded"
        onClick={() => dispatch(removeNotification(id))}
      >
        <XMarkIcon className="size-5" />
      </button>
    </div>
  );
};

export default Notification;
